import { PageNameEnum } from "types/enums";

export const getNumSlashes = pathname => (pathname.match(/\//g) || []).length;

/** get the PageNameEnum for the current page */
const getPageName = ({
  storyPage,
  isCustomerTilesPage,
  isTimelinePage,
  isHubletPage,
  isReportsPage,
  isProcessTilesPage,
  isSettingsPage,
  isTrendsPage,
  isGroupTilesPage,
  isGridLayoutPage,
  isOperatorPage,
  isHelpPage,
  isSchedulePage,
  isPowerBiPage,
}): PageNameEnum => {
  if (storyPage) {
    return storyPage;
  }

  const pageName = isCustomerTilesPage
    ? PageNameEnum.customerTiles
    : isProcessTilesPage
      ? PageNameEnum.processTiles
      : isGroupTilesPage
        ? PageNameEnum.groupTiles
        : isGridLayoutPage
          ? PageNameEnum.gridLayout
          : isOperatorPage
            ? PageNameEnum.operator
            : isTimelinePage
              ? PageNameEnum.timeline
              : isTrendsPage
                ? PageNameEnum.trends
                : isHubletPage
                  ? PageNameEnum.hublet
                  : isSettingsPage
                    ? PageNameEnum.settings
                    : isHelpPage
                      ? PageNameEnum.help
                      : isReportsPage
                        ? PageNameEnum.reports
                        : isSchedulePage
                          ? PageNameEnum.schedules
                          : isPowerBiPage
                            ? PageNameEnum.powerbi
                            : PageNameEnum.unknown;

  return pageName;
};

export type getPageFromPathReturnType = {
  numSlashes: number;
  isTimelinePage: boolean;
  isHubletPage: boolean;
  isSchedulePage: boolean;
  isProcessTilesPage: boolean;
  isGroupTilesPage: boolean;
  isSettingsPage: boolean;
  isHelpPage: boolean;
  isTrendsPage: boolean;
  isCustomerTilesPage: boolean;
  isGridLayoutPage: boolean;
  isOperatorPage: boolean;
  isReportsPage: boolean;
  isTier0Page?: boolean;
  isSnapshotsPage?: boolean;
  isFromSnapshotsPage?: boolean;
  isPowerBiPage?: boolean;
  isUnauthenticatedPage: boolean;
  pageName: PageNameEnum;
};
export const getPageFromPath = ({
  pathnameAndSearch,
  /** ignores the warning message about not having an associated pageName (use when pathnameAndSearch aren't realistic) */
  storyPage,
  mockNumSlashes,
}: {
  pathnameAndSearch: string;
  isTest?: boolean;
  storyPage?: string | null;
  mockNumSlashes?: number | null;
}): getPageFromPathReturnType => {
  const isCustomerTilesPage = pathnameAndSearch === "/";
  const isSchedulePage = pathnameAndSearch.includes("/schedule");
  const numSlashes = getNumSlashes(pathnameAndSearch);
  const isTimelinePage = pathnameAndSearch.includes("view=tl");
  const isHubletPage =
    pathnameAndSearch.includes("dashboard") && numSlashes === 4;
  const isProcessTilesPage =
    !isHubletPage &&
    !isTimelinePage &&
    pathnameAndSearch.includes("dashboard/") &&
    !pathnameAndSearch.endsWith("dashboard/") &&
    numSlashes === 3;
  const isSettingsPage = pathnameAndSearch.includes("/settings");
  const isHelpPage = pathnameAndSearch.includes("/help");
  const isTrendsPage = pathnameAndSearch.includes("/trends");
  const isGridLayoutPage = pathnameAndSearch.includes("/grid/");
  const isOperatorPage = pathnameAndSearch.includes("/operator/");
  const isReportsPage = pathnameAndSearch.includes("/reports");
  const isPowerBiPage = pathnameAndSearch.includes("/sight-analytics");
  const isGroupTilesPage =
    !isSettingsPage &&
    !isTimelinePage &&
    !isProcessTilesPage &&
    !isTrendsPage &&
    !isSchedulePage &&
    !isGridLayoutPage &&
    !isOperatorPage &&
    !isReportsPage &&
    !isPowerBiPage &&
    (numSlashes === 2 ||
      (numSlashes === 3 && pathnameAndSearch.endsWith("dashboard/")));
  const isUnauthenticatedPage = pathnameAndSearch.includes("/auth-failed");

  const pageName = getPageName({
    storyPage,
    isCustomerTilesPage,
    isTimelinePage,
    isHubletPage,
    isProcessTilesPage,
    isSettingsPage,
    isTrendsPage,
    isReportsPage,
    isGroupTilesPage,
    isGridLayoutPage,
    isOperatorPage,
    isHelpPage,
    isSchedulePage,
    isPowerBiPage,
  });

  const sp = storyPage;
  return {
    numSlashes: mockNumSlashes || numSlashes,
    // to mock what page we're on in storybook, pass e.g. {storyPage: PageNameEnum.timeline} into the global state
    isTimelinePage: sp ? sp === PageNameEnum.timeline : isTimelinePage,
    isHubletPage: sp ? sp === PageNameEnum.hublet : isHubletPage,
    isProcessTilesPage: sp
      ? sp === PageNameEnum.processTiles
      : isProcessTilesPage,
    isSchedulePage: sp ? sp === PageNameEnum.schedules : isSchedulePage,
    isGroupTilesPage: sp ? sp === PageNameEnum.groupTiles : isGroupTilesPage,
    isSettingsPage: sp ? sp === PageNameEnum.settings : isSettingsPage,
    isHelpPage: sp ? sp === PageNameEnum.help : isHelpPage,
    isTrendsPage: sp ? sp === PageNameEnum.trends : isTrendsPage,
    isCustomerTilesPage: sp
      ? sp === PageNameEnum.customerTiles
      : isCustomerTilesPage,
    isGridLayoutPage: sp ? sp === PageNameEnum.gridLayout : isGridLayoutPage,
    isOperatorPage: sp ? sp === PageNameEnum.operator : isOperatorPage,
    isReportsPage: sp ? sp === PageNameEnum.reports : isReportsPage,
    isUnauthenticatedPage,
    pageName,
  };
};

// back button goes up one level
// or to last location if we're on the settings top level
export const getBackButtonTooltip = ({
  upOneLevel,
  lastLocationPathStored,
  shouldGoBackNotUp,
}) => {
  const pathToReturnTo =
    shouldGoBackNotUp && lastLocationPathStored
      ? lastLocationPathStored
      : upOneLevel;

  const {
    isTimelinePage,
    isHubletPage,
    isProcessTilesPage,
    isGroupTilesPage,
    isSchedulePage,
    isCustomerTilesPage,
  } = getPageFromPath({ pathnameAndSearch: pathToReturnTo });
  const { isSettingsPage } = getPageFromPath({
    pathnameAndSearch: upOneLevel,
  });

  if (isTimelinePage) {
    return "Timeline";
  }
  if (isHubletPage) {
    return "Details";
  }
  if (isProcessTilesPage) {
    return "Process Tiles";
  }
  if (isGroupTilesPage) {
    return "Group Tiles";
  }
  if (isSettingsPage) {
    return "Settings";
  }
  if (isSchedulePage) {
    return "Schedules";
  }
  if (isCustomerTilesPage) {
    return "Customer Tiles";
  }
  return "previous page";
};
